import { VALIDATION_FALLBACK_MESSAGE, useAuthContext } from '@h2hspark/auth-ui';
import {
  MEMBER_REGISTRATION_STEPS,
  MEMBER_REGISTRATION_STEP_COUNT,
  MEMBER_REGISTRATION_WHY_WE_ASK,
  describePasswordFailure,
  inAppDepartureFor,
  memberRegistrationStepAt,
  validateStep,
  type MemberRegistrationAnswers,
  type MemberRegistrationStepId,
} from '@h2hspark/contracts';
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, WizardProgress } from '@h2hspark/ui';
import { Sparkles } from 'lucide-react';
import * as React from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useMemberSession } from '@/auth/auth-provider';

import { checkoutTargetOf } from '../event-orders/constants';
import { SignUpEventSummary } from '../event-orders/sign-up-event-summary-panel';

import {
  memberSessionExists,
  useCompleteRegistration,
  useRegistrationState,
  useRegistrationVocabularies,
  useRegistrationWizardContract,
  useSaveRegistrationStep,
  useStartRegistration,
  useMemberAppAdmission,
  useStartRegistrationForMyAccount,
  useOwnProfile,
} from './api';
import {
  REGISTRATION_ALREADY_A_MEMBER_ACTION,
  REGISTRATION_ALREADY_A_MEMBER_BODY,
  REGISTRATION_ADDRESS_ALREADY_TAKEN_ACTION,
  REGISTRATION_ADDRESS_ALREADY_TAKEN_ADDRESS_LABEL,
  REGISTRATION_ADDRESS_ALREADY_TAKEN_BODY,
  REGISTRATION_ADDRESS_ALREADY_TAKEN_TITLE,
  REGISTRATION_CANCEL_LABEL,
  REGISTRATION_EMAIL_INCOMPLETE,
  REGISTRATION_LOG_IN_ACTION,
  REGISTRATION_LOG_IN_PROMPT,
  REGISTRATION_NEEDS_SIGN_IN_ACTION,
  REGISTRATION_NEEDS_SIGN_IN_BODY,
  REGISTRATION_NEEDS_SIGN_IN_TITLE,
  checkStepOneEmailAddress,
  describeRegistrationFailure,
  registrationAlreadyAMemberTitle,
  registrationSignInPath,
  splitDisplayName,
} from './constants';
import {
  BasicsStep,
  ConnectingStep,
  GoalStep,
  InterestsStep,
  PrivacyStep,
} from './registration-step-form';

/**
 * The member registration wizard (`MEM-001`…`MEM-004`).
 *
 * Built from `Member App.dc.html`'s `showRegister` block (lines 64–128), which is the file that
 * owns registration — `Web App.dc.html`'s equivalent is the *reservation* flow (account → profile →
 * payment) belonging to `MEM-020`/`MEM-021`, a different stage.
 *
 * **Five steps since 2026-08-21**, when the client answered the four-versus-five question the
 * M1.1 pull request raised: use the prototype. `MEMBER_REGISTRATION_STEPS` carries the grouping and
 * why it is not the obvious one.
 *
 * ## Layout, transcribed
 *
 * ```
 * screen   min-height:812px; display:flex; flex-direction:column; padding-top:44px
 * header   the progress row — see @h2hspark/ui's WizardProgress
 * body     flex:1; padding:16px 20px
 * h1       --text-2xl; margin:0 0 6px
 * sub      --text-md; --text-muted; margin:0 0 {18|22|20|22|22}px  — see SUBTITLE_GAP
 * footer   padding:14px 20px 24px; border-top:1px solid --divider
 * button   width:100%; padding:16px; --radius-pill; font-brand 700; --text-md
 *          last step: --spark-gradient + --shadow-accent + sparkles icon, "Join H2HSpark"
 *          others:    --brand-primary, "Continue"
 * ```
 *
 * The `padding-top:44px` is the prototype's status-bar inset, which is the phone frame's rather
 * than the app's — the browser draws that chrome itself on a real device, so it is dropped and
 * noted here rather than reproduced as dead space.
 *
 * ## Saving, and why there is no `useEffect` writing anything
 *
 * **Continue is the save.** `save()` below is called from exactly two places: the footer button,
 * and the unsaved-changes prompt's save option. There is no debounce, no interval, no save on
 * blur and no save on `answers` changing — a search of this file for `setTimeout`, `setInterval`
 * or a save inside an effect finds nothing, and that is the requirement rather than an accident.
 *
 * `dirty` is the difference between what is on screen and what was last saved. It decides two
 * things: whether Continue sends anything at all, and whether leaving prompts. **A prompt on a
 * clean step is itself a defect** — it teaches a member to dismiss dialogs and the one that
 * mattered goes with it — so every departure below is guarded on `dirty`.
 *
 * The privacy step's three defaults are seeded **server-side** at step 1 for that reason; see
 * `MembersService.startRegistration`. A client-side default would make step 5 dirty on arrival.
 *
 * ## Step 1 has three shapes, not two
 *
 * Which one runs is decided by the session and by whether this account already has a registration,
 * and both questions are answered before Continue is enabled — see `waitingToKnowTheSession`.
 *
 * | The visitor | What step 1 shows | What Continue calls |
 * | --- | --- | --- |
 * | Signed out | The account fields, Google included | `POST /registration` — creates the account |
 * | Signed in, no registration | *Signed in as …*, and the professional fields | `POST /registration/for-my-account` |
 * | Signed in, mid-wizard | the same, with their stored answers | the ordinary `PATCH /registration` |
 *
 * **The middle row is a bug fix, not a new feature.** `MEM-002` AC-7 has always said an address
 * that already has an account resolves to that account rather than creating a second, and the
 * service has always done it — what was missing was a door a signed-in visitor could reach it
 * through, because step 1 was guarded on being signed *out*. An organizer pressing Continue on
 * `/app/join` therefore fell through to saving a step against a registration that did not exist,
 * the `PATCH` 404'd, and the rejection was swallowed by a `void onContinue()` with no catch. The
 * visible symptom was a button that did nothing at all.
 *
 * ## Nothing in this flow fails silently any more
 *
 * That last part is the more important half, and it is fixed independently of the bug that
 * exposed it: `onContinue` catches, the click handler catches behind it, the unsaved-changes
 * prompt refuses to depart on a failed save, and every one of those paths renders a `role="alert"`
 * sentence. A control that silently refuses teaches a person their input was wrong; it also hides
 * whatever breaks next, just as completely.
 */

/**
 * The subtitle's bottom margin, per step, from the markup.
 *
 * `rs0` is `margin:0 0 18px`, `rs2` is `20px`, and `rs1`/`rs3`/`rs4` are `22px`. Three different
 * values across five steps is exactly the kind of difference `building-from-designs.md` warns
 * reads as rounding — so it is transcribed per step rather than averaged to one number.
 */
const SUBTITLE_GAP: Record<MemberRegistrationStepId, string> = {
  basics: 'mb-[18px]',
  interests: 'mb-[22px]',
  goal: 'mb-[20px]',
  connecting: 'mb-[22px]',
  privacy: 'mb-[22px]',
};

export function MemberRegistrationWizardPage(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const session = useMemberSession();
  const { setToken } = useAuthContext();
  const signedIn = session.status === 'authenticated';

  /**
   * Whether there is an account behind this wizard yet — the gate on the two authenticated reads.
   *
   * **Not simply `signedIn`.** A member who has just completed step 1 has an account whether or
   * not this browser has managed to store a token: the Google path's session belongs to the social
   * exchange, and locally there is no identity provider at all, so the loopback dev-bypass answers
   * instead (ADR 015). Gating on `signedIn` alone left steps 2 to 5 unable to load their
   * vocabularies in exactly those two cases, which is a real gap and not only a local one.
   */
  const [hasAccount, setHasAccount] = React.useState(false);
  const authenticatedReadsEnabled = signedIn || hasAccount;

  /**
   * Step 1 completed but no session came out of it, so the wizard cannot go on.
   *
   * **Reachable from the signed-out path only.** A visitor who was already signed in has a session
   * by definition — it is what let them call `POST /registration/for-my-account` at all — so this
   * screen is not something an organizer becoming a member can be shown.
   *
   * **The reported bug, and what caused it.** Registering with an address that already has an
   * account — an organizer's, say — did nothing visible. Step 1 posted, the API answered `201`,
   * and the wizard advanced to step 2, which is authenticated: every read 401'd and every save
   * failed. The member saw a form that would not move and was told nothing.
   *
   * The API was behaving as designed. `MEM-002` AC-6 then forbade revealing whether an address had
   * an account, so `resolveOrCreate` returned the same shape either way and left `session` null
   * when it could not sign the member in. `directory-member-account-port.ts` even said what should
   * happen next — *"it failing leaves `session` null and the wizard asks them to sign in"* — and
   * the wizard never asked. That half was simply not built.
   *
   * ## Two reasons now, and the second is the one that can be honest
   *
   * `'no-session'` is the case above and keeps its careful wording: the address may have an
   * account, the password may have been mistyped, or there may be no identity provider here at all
   * (ADR 015), and the sentence has to be true in all three.
   *
   * `'address-already-has-an-account'` is `MEM-002` AC-6 as reworded on 2026-09-02. The API said
   * so outright — `409 AUTH_IDENTITY_ALREADY_EXISTS` — so there is nothing left to be careful
   * about and the screen says it. It is a separate reason rather than the same one worded
   * differently because the two are reached differently and one of them is a *fact* rather than a
   * hedge: collapsing them would mean guessing again on the path that must not guess.
   */
  const [couldNotContinue, setCouldNotContinue] = React.useState<
    null | 'no-session' | 'address-already-has-an-account'
  >(null);

  /**
   * Where a member who has **already finished** registration is offered on to (`MEM-001` AC-8,
   * Sumit 2026-09-21, TW 44145257).
   *
   * `null` while this is not that member, which is every visitor the wizard is for. It holds the
   * destination rather than a flag because the destination is the resume record's `returnTo` —
   * whatever the member was trying to do when something sent them here — and that is exactly what
   * the silent redirect used to pass to `navigate`. Nothing about where they end up changed.
   */
  const [alreadyAMemberDestination, setAlreadyAMemberDestination] = React.useState<string | null>(
    null,
  );

  // Public, and fetched from the first render: it carries the wizard's own shape, and since
  // `MEM-045` the terms step 1's optional selects need before an account exists.
  const contract = useRegistrationWizardContract();
  const state = useRegistrationState(authenticatedReadsEnabled);
  /**
   * The member's own name for the *already a member* screen (`MEM-001` AC-8).
   *
   * Asked for only once this is that member — `alreadyAMemberDestination` is set — so the wizard
   * a signed-out visitor opens makes no profile request, and neither does one that is resuming.
   */
  const ownProfile = useOwnProfile({ enabled: alreadyAMemberDestination !== null });
  const ownProfileShared = ownProfile.data?.shared;
  const alreadyAMemberName =
    ownProfileShared === undefined
      ? undefined
      : `${ownProfileShared.firstName} ${ownProfileShared.lastName}`.trim() || undefined;
  /**
   * The ask, when this account has no member profile at all (`USR-014` AC-2, AC-5).
   *
   * Fetched rather than inferred from `state.data === null`: the account state is `USR-008`'s and
   * is derived in one place, and a screen that worked it out from the resume read would be the
   * second definition. See `useMemberAppAdmission`.
   */
  const admission = useMemberAppAdmission(signedIn);
  const vocabularies = useRegistrationVocabularies(authenticatedReadsEnabled);
  const start = useStartRegistration();
  const startForMyAccount = useStartRegistrationForMyAccount();
  const saveStep = useSaveRegistrationStep();
  const complete = useCompleteRegistration();

  const [step, setStep] = React.useState(1);
  const [answers, setAnswers] = React.useState<MemberRegistrationAnswers>({});
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  // Nothing sets this today. The Google button (TW 44131761) now runs the shared social sign-up
  // and comes back to this wizard signed in, which `signedIn` handles; a code carried into step 1
  // is `USR-002`'s to build, and the plumbing for it is left where it was.
  const [googleInitCode] = React.useState<string | undefined>(undefined);
  const [errors, setErrors] = React.useState<Readonly<Record<string, string>>>({});
  const [whyOpen, setWhyOpen] = React.useState(false);
  const [prompt, setPrompt] = React.useState<null | { readonly departure: 'step-navigation' | 'wizard-exit'; readonly then: () => void }>(null);

  /**
   * What the last press of Continue failed with, or `null`.
   *
   * **The defect this state exists to end.** `onContinue` was invoked as `void onContinue()` with
   * no catch, so every rejection in this flow was swallowed: the button appeared to do nothing at
   * all. That is worse than any one bug behind it, because a control that silently refuses teaches
   * a person their input was wrong and sends them back to re-edit fields that were fine — and it
   * hides the *next* failure just as completely. Cleared at the start of every attempt, so the
   * message on screen is always about the press the member just made.
   */
  const [continueFailure, setContinueFailure] = React.useState<string | null>(null);

  /**
   * A member registration exists for this session's user — `undefined` until the resume read has
   * answered, and irrelevant while signed out.
   *
   * This is what decides which call step 1 makes, and it is the fact the wizard previously had no
   * way to hold: a signed-in visitor with no registration and a signed-in visitor mid-wizard both
   * looked like "signed in", so step 1 took the same branch for both and saved against a record
   * that did not exist.
   */
  const [startedHere, setStartedHere] = React.useState(false);
  const registrationExists: boolean | undefined = startedHere
    ? true
    : state.data === undefined
      ? undefined
      : state.data !== null;

  /** What was last saved. The baseline `dirty` is measured against. */
  const baseline = React.useRef<string>(JSON.stringify({}));
  const revision = React.useRef<string | undefined>(undefined);

  /**
   * What step 1's optional selects render from.
   *
   * `useRegistrationVocabularies` is gated on there being an account, because it is an
   * authenticated route — so on a first visit to step 1 it has no data and the two optional
   * professional fields would have nothing to offer. The public wizard contract carries their
   * terms for exactly that window. Preferring the authenticated answer once it exists means a
   * member who navigates back to step 1 mid-wizard sees one source rather than two.
   */
  const basicsVocabularies = React.useMemo(
    () =>
      vocabularies.data ??
      (contract.data === undefined
        ? undefined
        : { terms: contract.data.preAccountVocabularyTerms, unavailableVocabularies: [], blockedSteps: [] }),
    [vocabularies.data, contract.data],
  );

  /**
   * What steps 2 to 4 are given: `undefined` only while the read is in flight, so each step shows
   * a loading placeholder rather than telling a member it is not ready (TW 44261152). A read that
   * failed is given as empty, so the step says it is not ready instead of loading for ever.
   */
  const stepVocabularies = vocabularies.isError
    ? { terms: {}, unavailableVocabularies: [], blockedSteps: [] }
    : vocabularies.data;

  const dirty = JSON.stringify(answers) !== baseline.current;
  const stepDefinition = memberRegistrationStepAt(step) ?? MEMBER_REGISTRATION_STEPS[0]!;
  const stepId: MemberRegistrationStepId = stepDefinition.id;
  const isLastStep = step === MEMBER_REGISTRATION_STEP_COUNT;

  /**
   * The event this sign-up is for, when it is for one (`MEM-058` AC-5): read off the address on a
   * first visit and off the stored record on a resume, which is where `returnTo` survives a visitor
   * leaving and coming back.
   */
  const signUpTarget = checkoutTargetOf(searchParams.get('returnTo') ?? state.data?.returnTo);

  /**
   * Where signing in from here goes (`MEM-001` AC-9, Sumit 2026-09-28, TW 44216814): the login
   * page, told to come back to where this sign-up was going — the event's reservation, when
   * Reserve My Spot opened it. Step 1's *Log in* and the address-already-has-an-account screen's
   * *Sign in* both use it.
   */
  const signInPath = registrationSignInPath(searchParams.get('returnTo') ?? state.data?.returnTo);

  /**
   * Resume (`MEM-001` AC-5, AC-7).
   *
   * The only effect that touches state, and it *reads* — it adopts the stored record as the
   * starting point. Nothing here writes, on a timer or otherwise.
   */
  React.useEffect(() => {
    const record = state.data;
    // `undefined` is "the read has not answered"; `null` is "there is nothing to resume", which
    // is the ordinary state of every account holder who has never been a member. Neither is a
    // record to adopt, and neither is a failure.
    if (record === undefined || record === null) return;
    if (record.status === 'active') {
      // **This used to be `navigate(record.returnTo, { replace: true })`, and that is the report**
      // (TW 44145257, `MEM-001` AC-8). An account with nothing left to register was moved on
      // without a word — and from an app page, "on" was the page it was already showing, so Sign
      // up looked like a link that refreshed. The destination is unchanged; it is now behind a
      // sentence and a button rather than behind nothing at all. See
      // `REGISTRATION_ALREADY_A_MEMBER_TITLE`.
      setAlreadyAMemberDestination(record.returnTo);
      return;
    }
    setAnswers(record.answers);
    setStep(record.resumeStep);
    baseline.current = JSON.stringify(record.answers);
    revision.current = record.revision;
  }, [state.data, navigate]);

  /**
   * Prefill the name from the account, for a signed-in visitor with nothing to resume.
   *
   * The client's ruling is that an existing account *finishes* registration rather than starting
   * one, so identity the platform already holds should not be typed again. `displayName` is what
   * the session carries; role and company are not on an account at all, which is why step 1 still
   * renders rather than being skipped (`MEM-002` AC-3's argument, applied at a different door).
   *
   * **The baseline moves with it**, and that is not incidental. `dirty` is the difference between
   * the screen and the last saved state, and a prefill the member did not type must not make step
   * 1 dirty on arrival — going Back would then raise the unsaved-changes prompt over a value
   * nobody had chosen, which this file already calls a defect where the privacy defaults are
   * concerned. Same rule, same reason.
   *
   * Runs once: `answers` being empty is the guard, so a member who clears the field keeps it
   * cleared rather than having it typed back in under them.
   */
  React.useEffect(() => {
    if (!signedIn) return;
    if (registrationExists !== false) return;
    const displayName = session.session?.displayName;
    if (displayName === undefined || displayName.trim().length === 0) return;
    if (answers.firstName !== undefined || answers.lastName !== undefined) return;
    const { firstName, lastName } = splitDisplayName(displayName);
    if (firstName.length === 0) return;
    const prefilled = { ...answers, firstName, ...(lastName.length > 0 ? { lastName } : {}) };
    // Before the state update, because `dirty` is computed during render from this ref: setting
    // both together is what makes the prefilled step 1 arrive *clean*.
    baseline.current = JSON.stringify(prefilled);
    setAnswers(prefilled);
  }, [signedIn, registrationExists, session.session?.displayName, answers]);

  /**
   * The tab-close departure (`MEM-001`'s explicit-saving rule, mirroring `EVT-002` AC-5).
   *
   * Registered only while dirty and removed by a successful save. Browsers render their own
   * dialog here with their own two buttons — there is no Save option in it, because a save cannot
   * be awaited at `beforeunload` — so what is ours is the handler's *presence*, and that is all
   * this does.
   */
  React.useEffect(() => {
    if (!dirty) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
    };
  }, [dirty]);

  function patch(next: MemberRegistrationAnswers): void {
    setAnswers((current) => ({ ...current, ...next }));
    // Clearing a field's error as it is edited, rather than waiting for the next Continue. The
    // error was about the value that has just changed.
    setErrors((current) => {
      const remaining = { ...current };
      for (const field of Object.keys(next)) delete remaining[field];
      return remaining;
    });
  }

  /** Per-step validation (`MEM-001` AC-2, AC-3). Marks every failing field and focuses the first. */
  function validateAndFocus(): boolean {
    const failures = [...validateStep(stepId, answers)];
    // The account's own two fields, and only where step 1 is actually collecting them. A signed-in
    // visitor is not shown an email or a password field, so demanding either would be the wizard
    // refusing to advance over a control the member cannot see — which is the same silent-refusal
    // shape as the bug this file is fixing, arriving from the other side.
    if (step === 1 && !signedIn) {
      // Checked with the server's own rule (`MEM-059` AC-1), not only for being there: the input
      // is `type="email"`, but the browser's check does not run on this form, so a space inside
      // the address, a trailing dot or an invisible character pasted in with it was sent, refused
      // with a 400, and reported as "press Continue to try again" — for ever.
      if (email.trim().length === 0) failures.push({ field: 'email', message: 'This one is needed.' });
      else if (!checkStepOneEmailAddress(email).ok) {
        failures.push({ field: 'email', message: REGISTRATION_EMAIL_INCOMPLETE });
      }
      if (googleInitCode === undefined) {
        const passwordFailure = describePasswordFailure(password);
        if (passwordFailure !== null) failures.push({ field: 'password', message: passwordFailure });
      }
    }
    if (failures.length === 0) {
      setErrors({});
      return true;
    }
    setErrors(Object.fromEntries(failures.map((failure) => [failure.field, failure.message])));
    // The step is not advanced, and the first failing control takes focus.
    const first = failures[0]?.field;
    if (first !== undefined) {
      window.setTimeout(() => {
        document.getElementById(first)?.focus();
      }, 0);
    }
    return false;
  }

  /**
   * The save. Called from Continue and from the prompt, and from nowhere else.
   *
   * `ok` is whether the wizard may carry on. `moved` is whether the save has **already** put the
   * wizard on its next step — the two step-1 branches do, because the server tells them which step
   * the member resumes at, and the ordinary per-step save does not. Returning it is what replaced
   * `onContinue`'s old `if (step !== 1 || signedIn)`, a condition that happened to be right only
   * because step 1's signed-in case did nothing at all.
   */
  async function save(): Promise<{ readonly ok: boolean; readonly moved: boolean }> {
    if (step === 1 && !signedIn) {
      // The address as the server's rule reads it — without the spaces around it (`MEM-059`
      // AC-2). `validateAndFocus` has already refused one the rule does not accept, so the
      // fallback to what was typed is for a caller that skipped it, and the server refuses that.
      const checked = checkStepOneEmailAddress(email);
      const started = await start.mutateAsync({
        firstName: answers.firstName ?? '',
        lastName: answers.lastName ?? '',
        role: answers.role ?? '',
        company: answers.company ?? '',
        email: checked.ok ? checked.address : email,
        ...(googleInitCode === undefined ? { password } : { googleInitCode }),
        ...(searchParams.get('returnTo') !== null ? { returnTo: searchParams.get('returnTo')! } : {}),
      });
      baseline.current = JSON.stringify(answers);
      // The account now exists, and steps 2 to 5 are authenticated — so the session step 1 handed
      // back is stored before anything else runs. Without this the wizard reaches step 2 and can
      // neither load its vocabularies nor save it.
      // `?.` rather than a `!== null` check: locally there is no identity provider at all (ADR
      // 015), so the field is legitimately absent as well as legitimately null, and the wizard
      // carries on either way — the loopback dev-bypass supplies the session there.
      const granted = started.session?.sessionToken;
      if (granted !== undefined) setToken(granted);
      await session.reload();

      // **Asked of the server, not inferred from the response.** `session: null` means two
      // different things — "this address already has an account and the password did not match"
      // and "there is no identity provider here at all", which is ordinary local development
      // where the loopback dev-bypass answers `/auth/me` instead (ADR 015). Only one of them
      // should stop the wizard, and the response cannot tell them apart *by design*: a field that
      // distinguished them would be the enumeration leak `MEM-002` AC-6 forbids.
      //
      // Whether a session exists now is a question with one answer, and the server has it.
      if (!(await memberSessionExists())) {
        setCouldNotContinue('no-session');
        return { ok: false, moved: false };
      }

      // The account exists from here on, which is what steps 2 to 5 need to know before they read.
      setHasAccount(true);
      setStartedHere(true);
      setStep(started.currentStep);
      return { ok: true, moved: true };
    }

    /**
     * Step 1 for a visitor who **already had an account** (`MEM-002` AC-7, `MEM-001` AC-7).
     *
     * This branch is the bug. It did not exist: step 1 was guarded on being signed *out*, so a
     * signed-in visitor fell straight through to the per-step save below and `PATCH` 404'd against
     * a registration that had never been created. Nothing was made, nothing was said, and the
     * button looked broken.
     *
     * `registrationExists === false` and not merely `signedIn`, because a member half-way through
     * the wizard who navigates back to step 1 is also signed in and must take the ordinary save —
     * they already have the record this call would create.
     *
     * No credential is sent and no address is sent. The server reads the member from the session.
     */
    if (step === 1 && registrationExists === false) {
      const started = await startForMyAccount.mutateAsync({
        firstName: answers.firstName ?? '',
        lastName: answers.lastName ?? '',
        role: answers.role ?? '',
        company: answers.company ?? '',
        ...(answers.industryTermId !== undefined ? { industryTermId: answers.industryTermId } : {}),
        ...(answers.careerStageTermId !== undefined
          ? { careerStageTermId: answers.careerStageTermId }
          : {}),
        ...(searchParams.get('returnTo') !== null ? { returnTo: searchParams.get('returnTo')! } : {}),
      });
      baseline.current = JSON.stringify(answers);
      // There is a registration now, so a second press of Continue on step 1 saves rather than
      // starting again — and steps 2 to 5 may read.
      setStartedHere(true);
      setHasAccount(true);
      setStep(started.currentStep);
      // **Read it back** (TW 44296585, `MEM-003` AC-4). The resume read already answered 404 on
      // this door and would never ask again, so the wizard carried on from its own copy of step 1
      // — which has none of the privacy elections the server seeded. Step 5 drew all three off
      // while the record held (a) and (b) on, and an untouched step 5 saves nothing, so the member
      // was opted in under boxes that said otherwise. The signed-out door gets this for free:
      // signing in is what first enables the read.
      await state.refetch();
      return { ok: true, moved: true };
    }

    // A clean step sends nothing at all. The server would also answer `written: false` for free,
    // but not issuing the request is the point of holding a dirty flag.
    if (!dirty) return { ok: true, moved: false };

    const outcome = await saveStep.mutateAsync({
      step: stepId,
      answers,
      ...(revision.current !== undefined ? { ifRevision: revision.current } : {}),
    });
    baseline.current = JSON.stringify(answers);
    revision.current = outcome.revision;
    return { ok: true, moved: false };
  }

  /**
   * Continue.
   *
   * **Every rejection below is caught, and that is the point of the shape.** This used to be
   * called as `void onContinue()` with nothing behind it, so a failed call left the member looking
   * at a button that did nothing — no message, no navigation, no console entry they would ever
   * see. The `try` is what makes each of those failures a sentence on screen instead.
   */
  /**
   * A rejection, put on the screen it belongs on.
   *
   * The three catch sites below all go through here, because the choice between *a sentence above
   * the button* and *a screen with a way out of this* has to be made the same way in all of them —
   * and the reported loop was a failure that reached only the first of those two.
   */
  function reportFailure(error: unknown): void {
    const failure = describeRegistrationFailure(error);
    if (failure.kind === 'address-already-has-an-account') {
      // `MEM-002` AC-6. Not a sentence over a Continue button: pressing Continue again refuses
      // identically for ever, so the wizard gives way to the screen that says where to go.
      setCouldNotContinue('address-already-has-an-account');
      return;
    }
    if (failure.kind === 'invalid') {
      // `MEM-059` AC-3. A 400 is refused identically however often Continue is pressed, so it is
      // never the retry sentence. Where the refusal is about the address and the address is on
      // screen, it goes on that field, as the wizard's own check would have put it; otherwise the
      // sign-in forms' sentence asks the member to check what they entered.
      if (failure.field === 'email' && step === 1 && !signedIn) {
        setPrompt(null);
        setErrors((current) => ({ ...current, email: REGISTRATION_EMAIL_INCOMPLETE }));
        window.setTimeout(() => {
          document.getElementById('email')?.focus();
        }, 0);
        return;
      }
      setContinueFailure(VALIDATION_FALLBACK_MESSAGE);
      return;
    }
    setContinueFailure(failure.message);
  }

  async function onContinue(): Promise<void> {
    setContinueFailure(null);
    if (!validateAndFocus()) return;
    try {
      const saved = await save();
      // A refusal the wizard has already rendered its own screen for — `couldNotContinue`. Nothing
      // more to say, and certainly not "press Continue to try again".
      if (!saved.ok) return;
      if (isLastStep) {
        const finished = await complete.mutateAsync();
        // Back to whatever they were trying to do (`MEM-001` AC-6), not to a generic home.
        navigate(finished.returnTo, { replace: true });
        return;
      }
      if (!saved.moved) setStep((current) => Math.min(current + 1, MEMBER_REGISTRATION_STEP_COUNT));
    } catch (error) {
      reportFailure(error);
    }
  }

  /** Going back, and leaving. Both prompt while dirty; neither prompts while clean. */
  function leave(departure: 'step-navigation' | 'wizard-exit', then: () => void): void {
    if (dirty) {
      setPrompt({ departure, then });
      return;
    }
    then();
  }

  function onBack(): void {
    leave('step-navigation', () => {
      if (step === 1) {
        navigate('/app/login');
        return;
      }
      setStep((current) => current - 1);
      setErrors({});
    });
  }

  /**
   * **Cancel — the way out of sign-up** (TW 44131717, approved by Sumit 2026-09-18).
   *
   * Back to wherever the person came from inside the site, or to the homepage when they arrived
   * from outside it (a shared link, a new tab, a reload). React Router keys the first entry of a
   * session `'default'`, and every entry reached by navigating inside the app gets a key of its
   * own, so a non-default key is the evidence that "back" is somewhere on this site.
   *
   * Signed out on step 1 there is no account and nothing saved, so it leaves at once — the
   * unsaved-changes prompt's *Save* there would create an account, which is the opposite of
   * cancelling. From step 2 the account exists, and a dirty step asks first, like any other exit.
   */
  function onCancel(): void {
    const exit = (): void => {
      if (location.key !== 'default') navigate(-1);
      else navigate('/');
    };
    if (step === 1 && !signedIn) {
      exit();
      return;
    }
    leave('wizard-exit', exit);
  }

  const promptCopy = inAppDepartureFor(prompt?.departure);
  const whyWeAsk = MEMBER_REGISTRATION_WHY_WE_ASK[stepId];
  const blocked = vocabularies.data?.blockedSteps.includes(stepId) ?? false;

  /**
   * Step 1 does not yet know which of its two calls it would make.
   *
   * True while `/auth/me` is still answering, and while a signed-in visitor's resume read is still
   * in flight. Both windows are short and both are real, and a press inside either would have to
   * guess — so Continue waits instead. Steps 2 to 5 are unaffected: by then the answer is in.
   */
  const waitingToKnowTheSession =
    step === 1 && (session.status === 'loading' || (signedIn && registrationExists === undefined));

  /**
   * **Already a member** (`MEM-001` AC-8, Sumit 2026-09-21, TW 44145257).
   *
   * Ahead of `couldNotContinue` because it is not a refusal and there is nothing to refuse: this
   * account finished registration, and the wizard has no question left to ask it. It takes the
   * same screen as the two refusals — one column, centred, the reason and one way onward — so the
   * three doors out of step 1 look like three doors out of one place rather than three screens.
   *
   * The action goes where the redirect went, so a member who presses it is exactly where the old
   * behaviour would have put them, a sentence later.
   */
  if (alreadyAMemberDestination !== null) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-app-form flex-col justify-center gap-4 bg-background px-6 text-center">
        <h1 className="text-2xl text-foreground">
          {/* The **profile's** name, and the session's only as a stand-in — the same rule the
              avatar follows (TW 44131893). A member who renamed themselves on Your account is
              named here as they named themselves, not as the identity provider stored them at
              sign-up. There is always a profile to read on this branch: it is reached only for an
              account whose registration is `active`. */}
          {registrationAlreadyAMemberTitle(alreadyAMemberName ?? session.session?.displayName)}
        </h1>
        <p className="text-md text-muted-foreground">{REGISTRATION_ALREADY_A_MEMBER_BODY}</p>
        <Button
          className="mt-2 w-full rounded-pill"
          onClick={() => navigate(alreadyAMemberDestination, { replace: true })}
        >
          {REGISTRATION_ALREADY_A_MEMBER_ACTION}
        </Button>
      </div>
    );
  }

  // Step 1 cannot go on, and the wizard gives way to the reason. Rendered instead of the wizard
  // rather than beside it: leaving the form on screen behind a message is what made this look like
  // a form that would not move.
  //
  // Both reasons end at the same door, which is the point of them being one screen: the way out of
  // "this address has an account" and the way out of "no session came back" is to sign in.
  if (couldNotContinue !== null) {
    const taken = couldNotContinue === 'address-already-has-an-account';
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-app-form flex-col justify-center gap-4 bg-background px-6 text-center">
        <h1 className="text-2xl text-foreground">
          {taken ? REGISTRATION_ADDRESS_ALREADY_TAKEN_TITLE : REGISTRATION_NEEDS_SIGN_IN_TITLE}
        </h1>
        <p className="text-md text-muted-foreground">
          {taken ? REGISTRATION_ADDRESS_ALREADY_TAKEN_BODY : REGISTRATION_NEEDS_SIGN_IN_BODY}
        </p>
        {/* The address the caller just typed, echoed back — `MEM-002` AC-6's *"the address it
            echoes is the one the caller just sent"*. It discloses nothing they did not type a
            moment ago, and it is how somebody who fat-fingered their address sees that they did. */}
        {taken && email.length > 0 ? (
          <p className="text-sm text-text-subtle">
            {REGISTRATION_ADDRESS_ALREADY_TAKEN_ADDRESS_LABEL} <strong>{email}</strong>
          </p>
        ) : null}
        <Button
          className="mt-2 w-full rounded-pill"
          onClick={() => navigate(signInPath, { replace: true })}
        >
          {taken ? REGISTRATION_ADDRESS_ALREADY_TAKEN_ACTION : REGISTRATION_NEEDS_SIGN_IN_ACTION}
        </Button>
      </div>
    );
  }

  return (
    /*
     * `max-w-app-form`, not `max-w-app-canvas` (`MEM-058` AC-4, `MEM-056` AC-6).
     *
     * The wizard renders **outside** `MemberLayout`, so it has no container but its own, and its
     * own was the phone shell's 390px — a phone screen stretched down the middle of a laptop,
     * which is exactly what the ruling of 2026-09-11 says the member app is not. The token is
     * 600px: the prototype's own figure for a setup column, and a line length a form can be read
     * at. On a phone the form is the viewport and nothing changes, which is why one token serves
     * both widths.
     *
     * **Two panes when the sign-up is for an event** (`MEM-058` AC-5, Sumit 2026-09-24,
     * TW 44159462). The prototype's reservation flow draws the order summary beside every step,
     * account creation included. Ours signs up and pays on separate addresses (`MEM-TKT-010` needs
     * an address to return a signed-out visitor to), so the event is read back out of `returnTo`
     * — see `checkoutTargetOf` — and `SignUpBesideTheEvent` puts its card beside this column.
     * A sign-up that is not for an event is this column alone, as it always was.
     */
    <SignUpBesideTheEvent target={signUpTarget}>
    <div className="mx-auto flex min-h-screen w-full max-w-app-form flex-col bg-background">
      {/*
        The way out (TW 44131717, Sumit 2026-09-18). Sign-up stays focused — no site header, no
        footer — but it is not a room without a door: the logotype goes home and Cancel goes back
        to where the person came from, the logotype-and-Cancel bar the prototype draws over its own
        reservation funnel (§3.1). A departure from the registration screens' prototype, which
        draws neither; recorded in `MEM-001`'s notes.
      */}
      <header className="flex h-14 items-center justify-between px-5">
        <Link
          to="/"
          className="rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
        >
          <img src="/brand/logotype.png" alt="H2HSpark home" className="h-5 w-auto" />
        </Link>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md font-brand text-sm font-bold text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
        >
          {REGISTRATION_CANCEL_LABEL}
        </button>
      </header>
      <WizardProgress step={step} total={MEMBER_REGISTRATION_STEP_COUNT} onBack={onBack} />

      <div className="flex-1 px-5 py-4">
        <h1 className="mb-[6px] text-2xl text-foreground">{stepDefinition.title}</h1>
        <p className={`${SUBTITLE_GAP[stepId]} text-md text-muted-foreground`}>
          {stepDefinition.subtitle}{' '}
          {/* "Why we ask" on every step, not just step 1 (`MEM-001` AC-4). The prototype has it on
              step 1 only; the story requires it throughout, and says why. */}
          <button
            type="button"
            onClick={() => setWhyOpen(true)}
            className="font-semibold text-primary underline-offset-2 hover:underline"
          >
            Why we ask
          </button>
        </p>

        {step === 1 ? (
          <BasicsStep
            answers={answers}
            errors={errors}
            onChange={patch}
            // Step 1 reads the taxonomy since `MEM-045` — the two optional professional fields
            // select from it. **From the public wizard contract, not the authenticated route**:
            // this step runs before the account exists, so the authenticated one has not been
            // fetched yet and never will be in time. Once an account does exist (a member who came
            // back to step 1) the authenticated terms are preferred, which keeps one source once
            // there is one to have.
            vocabularies={basicsVocabularies}
            email={email}
            password={password}
            onEmailChange={(next) => {
              setEmail(next);
              // As `patch` does for every other field: the error was about the value just changed.
              setErrors((current) => {
                if (current['email'] === undefined) return current;
                const remaining = { ...current };
                delete remaining['email'];
                return remaining;
              });
            }}
            onPasswordChange={setPassword}
            // The account, when there already is one (`MEM-002` AC-7). Present replaces the whole
            // account half of this step with a statement of who is signed in — see `BasicsStep`.
            // The address comes from the reader's own session, so it discloses nothing about
            // anybody they are not already.
            {...(signedIn && session.session !== null
              ? { signedInAs: session.session.emailAddress ?? session.session.displayName ?? '' }
              : {})}
            // `USR-014` AC-5. Present only while the server says a member profile is outstanding,
            // so a member resuming the wizard is not asked for something they are already doing.
            {...(admission.data?.ask !== null && admission.data?.ask !== undefined
              ? { profileAsk: admission.data.ask }
              : {})}
            googleLinked={googleInitCode !== undefined}
          />
        ) : step === 2 ? (
          <InterestsStep answers={answers} errors={errors} onChange={patch} vocabularies={stepVocabularies} />
        ) : step === 3 ? (
          <GoalStep answers={answers} errors={errors} onChange={patch} vocabularies={stepVocabularies} />
        ) : step === 4 ? (
          <ConnectingStep answers={answers} errors={errors} onChange={patch} vocabularies={stepVocabularies} />
        ) : (
          <PrivacyStep answers={answers} errors={errors} onChange={patch} />
        )}
      </div>

      {/* footer: 14px 20px 24px, 1px divider rule above */}
      <div className="border-t border-divider px-5 pb-6 pt-[14px]">
        {/* What the last press failed with. `role="alert"` so it is announced rather than only
            drawn, and above the button because that is the control it is about. */}
        {continueFailure === null ? null : (
          <p
            role="alert"
            className="mb-[14px] text-center text-sm font-semibold text-destructive"
          >
            {continueFailure}
          </p>
        )}
        <button
          type="button"
          disabled={
            blocked ||
            saveStep.isPending ||
            start.isPending ||
            startForMyAccount.isPending ||
            complete.isPending ||
            // Step 1 cannot decide which call to make until it knows whether this session already
            // has a registration. Disabled rather than left to guess: a press in that window
            // would have taken the wrong branch, and the wrong branch is the reported bug.
            waitingToKnowTheSession
          }
          onClick={() => {
            // `.catch` rather than `void`. `onContinue` handles its own failures, and this is the
            // belt on that braces: a rejection escaping it must never again reach the platform as
            // an unhandled promise and the member as nothing at all.
            onContinue().catch((error: unknown) => {
              reportFailure(error);
            });
          }}
          className={
            isLastStep
              ? 'inline-flex w-full items-center justify-center gap-2 rounded-pill bg-spark p-4 font-brand text-md font-bold text-white shadow-accent disabled:opacity-50'
              : 'w-full rounded-pill bg-primary p-4 font-brand text-md font-bold text-primary-foreground disabled:opacity-50'
          }
        >
          {isLastStep ? <Sparkles className="h-[18px] w-[18px]" aria-hidden /> : null}
          {stepDefinition.action}
        </button>
        {/* An existing member's way in (`MEM-001` AC-9, TW 44216814). Not offered to somebody who
            is already signed in: step 1 already says whose account this is. */}
        {step === 1 && !signedIn ? (
          <p className="mt-[14px] text-center text-sm text-muted-foreground">
            {REGISTRATION_LOG_IN_PROMPT}{' '}
            <Link
              to={signInPath}
              className="rounded-md font-semibold text-primary underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
            >
              {REGISTRATION_LOG_IN_ACTION}
            </Link>
          </p>
        ) : null}
        {step === 1 ? (
          <p className="mt-[14px] text-center text-xs text-text-subtle">
            By continuing you agree to our Terms &amp; Privacy Policy.
          </p>
        ) : null}
      </div>

      {/* "Why we ask" — an overlay, so it never leaves the wizard (`MEM-001` AC-4). */}
      <Dialog open={whyOpen} onOpenChange={setWhyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{whyWeAsk.heading}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            {whyWeAsk.body.map((paragraph) => (
              <p key={paragraph} className="text-sm leading-normal text-muted-foreground">
                {paragraph}
              </p>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* The unsaved-changes prompt. Reachable only while dirty — `prompt` is set nowhere else. */}
      <Dialog open={prompt !== null} onOpenChange={(open) => !open && setPrompt(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{promptCopy?.heading}</DialogTitle>
            <DialogDescription>{promptCopy?.body}</DialogDescription>
          </DialogHeader>
          {/* A failed save from inside this dialogue, said here rather than in the footer behind
              it. The prompt stays open, so the member's next move is still the one that keeps
              their changes. */}
          {continueFailure === null ? null : (
            <p role="alert" className="text-sm font-semibold text-destructive">
              {continueFailure}
            </p>
          )}
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className="w-full rounded-pill bg-primary p-3 font-brand text-sm font-bold text-primary-foreground"
              onClick={() => {
                // The prompt's save, with the same rule Continue now follows: a failure here is
                // said out loud rather than dropped. It used to be a bare `void (async …)()`, so
                // "Save and continue" could fail and depart anyway — losing the very changes the
                // dialogue existed to protect, silently.
                void (async () => {
                  if (!validateAndFocus()) {
                    setPrompt(null);
                    return;
                  }
                  setContinueFailure(null);
                  try {
                    const saved = await save();
                    if (!saved.ok) return;
                  } catch (error) {
                    // The prompt stays open and the departure does not happen. Leaving while the
                    // save had failed is the one outcome this dialogue must never produce.
                    reportFailure(error);
                    return;
                  }
                  const then = prompt?.then;
                  setPrompt(null);
                  then?.();
                })();
              }}
            >
              {promptCopy?.save ?? 'Save'}
            </button>
            <button
              type="button"
              className="w-full rounded-pill border-[1.5px] border-border p-3 font-brand text-sm font-bold text-text-body"
              onClick={() => {
                // Discard: fall back to the last saved answers rather than keeping what was typed.
                setAnswers(JSON.parse(baseline.current) as MemberRegistrationAnswers);
                const then = prompt?.then;
                setPrompt(null);
                then?.();
              }}
            >
              {promptCopy?.discard ?? 'Discard changes'}
            </button>
            <button
              type="button"
              className="w-full p-3 font-brand text-sm font-semibold text-muted-foreground"
              onClick={() => setPrompt(null)}
            >
              {promptCopy?.cancel ?? 'Stay'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </SignUpBesideTheEvent>
  );
}

/**
 * The sign-up column, and beside it the event it is for (`MEM-058` AC-5).
 *
 * The checkout's own grid — `1.5fr 1fr` from `lg`, one column below it — for the checkout's
 * measured reason: at 768 a split leaves the card narrower than its own total line. Below `lg` the
 * card follows the form, the same reading order the checkout keeps (`MEM-058` AC-1); Sumit, on
 * TW 44159462: *"On phone it can stack."* The column gives up its own full-screen height here
 * (`[&>:first-child]:min-h-0`), or a short step would leave a screen of blank page between its
 * button and the card; the wrapper keeps the height instead. With no event it renders the column
 * untouched.
 */
function SignUpBesideTheEvent({
  target,
  children,
}: {
  readonly target: ReturnType<typeof checkoutTargetOf>;
  readonly children: React.ReactNode;
}): React.ReactElement {
  if (target === null) return <>{children}</>;
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto grid w-full max-w-app-desktop grid-cols-1 items-start [&>:first-child]:min-h-0 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:gap-10 lg:px-8">
        {children}
        <aside className="px-5 pb-10 lg:px-0 lg:pb-0 lg:pt-16">
          <SignUpEventSummary eventId={target.eventId} ticketTypeId={target.ticketTypeId} />
        </aside>
      </div>
    </div>
  );
}
